/**
 * Admin AI Rate Limit Agent.
 *
 * Provides admin-only hooks for listing current AI rate-limit buckets and
 * resetting individual user or anonymous quotas.
 */

import { useCallback, useEffect, useState } from 'react';
import APIAgent from './apiAgent';

const ENDPOINT = '/api/admin/ai-rate-limits';

export function useAdminAIRateLimits() {
  const [settings, setSettings] = useState({ enabled: false, max_requests: 0, window_seconds: 0 });
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await APIAgent.request(ENDPOINT, 'GET');
      setSettings(data?.settings || { enabled: false, max_requests: 0, window_seconds: 0 });
      setEntries(Array.isArray(data?.entries) ? data.entries : []);
      setIsAdmin(true);
    } catch (err) {
      const msg = err?.message || '';
      if (msg.includes('403') || msg.includes('401') || msg.toLowerCase().includes('admin')) {
        setIsAdmin(false);
      } else {
        setError(msg || 'Failed to load AI rate limits');
        setIsAdmin(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const resetBucket = useCallback(async (identityKey) => {
    await APIAgent.request(`${ENDPOINT}/${encodeURIComponent(identityKey)}`, 'DELETE');
    await refresh();
  }, [refresh]);

  return {
    settings,
    entries,
    loading,
    error,
    isAdmin,
    refresh,
    resetBucket,
  };
}
